/**
 * ErrorReporter — Captures errors and sends to Imagia bug tracker
 *
 * Captures:
 * 1. React ErrorBoundary — render/lifecycle crashes
 * 2. window.onerror — uncaught JS errors
 * 3. window.onunhandledrejection — unhandled promise rejections
 * 4. reportFeatureError() — explicit feature failures (call failed, API broken, etc.)
 * 5. API failure tracking — repeated 5xx from the same endpoint
 */

import React from 'react';

const IMAGIA_API = import.meta.env.VITE_IMAGIA_API_URL || 'https://imagia.net';
const PARTNER_KEY = import.meta.env.VITE_IMAGIA_PARTNER_KEY || '';

// Debounce: don't report the same error more than once per 5 min
const reported = new Map();
const DEBOUNCE_MS = 300000;

// API failure tracking: report after 3 consecutive 5xx from the same endpoint
const apiFailures = new Map();

export function sendBugReport({ title, description, stackTrace, errorType, url, filePath, severity, source }) {
  // Normalize dedup key: strip line numbers, timestamps, and dynamic parts
  const normalizedTitle = (title || '').replace(/:\d+:\d+/g, '').replace(/\d{4}-\d{2}-\d{2}/g, '').substring(0, 100);
  const key = `${errorType}:${normalizedTitle}`;
  if (reported.has(key) && Date.now() - reported.get(key) < DEBOUNCE_MS) return;
  reported.set(key, Date.now());

  fetch(`${IMAGIA_API}/api/partner/bugs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Partner-Key': PARTNER_KEY },
    body: JSON.stringify({
      source: source || 'frontend_error',
      source_app: 'jubly',
      severity: severity || undefined,
      title: title?.substring(0, 500),
      description,
      stack_trace: stackTrace?.substring(0, 3000),
      error_type: errorType,
      url: url || window.location.href,
      user_agent: navigator.userAgent,
      file_path: filePath,
      reporter_email: 'ben@hapi.vc',
      auto_fix: true,
    }),
  }).catch(() => {}); // fire and forget
}

// Install global handlers (call once at app startup)
export function installGlobalErrorHandlers() {
  // Uncaught JS errors
  window.onerror = (message, source, lineno, colno, error) => {
    sendBugReport({
      title: `${error?.name || 'Error'}: ${message}`,
      description: `at ${source}:${lineno}:${colno}`,
      stackTrace: error?.stack,
      errorType: error?.name || 'UncaughtError',
      filePath: source,
    });
    return false; // don't suppress default handling
  };

  // Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    const message = error?.message || String(error);
    // Skip network errors (API 4xx/5xx) — those are expected
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) return;

    sendBugReport({
      title: `UnhandledRejection: ${message.substring(0, 200)}`,
      description: error?.stack?.split('\n').slice(0, 5).join('\n'),
      stackTrace: error?.stack,
      errorType: 'UnhandledPromiseRejection',
    });
  };
}

/**
 * Report a feature-level error explicitly.
 * Call this from specific feature code when operations fail in ways that
 * don't throw JS errors (e.g., Telnyx call rejected, API returns error response).
 *
 * Usage:
 *   import { reportFeatureError } from './ErrorReporter';
 *   reportFeatureError('voice', 'Call failed: SIP 488 Media Encryption Required', {
 *     sipCode: 488, sipReason: 'Media Encryption Required B3', direction: 'outbound'
 *   });
 */
export function reportFeatureError(feature, message, details = {}) {
  sendBugReport({
    title: `[${feature}] ${message}`.substring(0, 500),
    description: JSON.stringify(details, null, 2),
    errorType: `FeatureError:${feature}`,
    severity: details.severity || 'high',
    source: 'frontend_error',
  });
}

/**
 * Track API response failures. Call after every fetch.
 * Reports a bug after 3 consecutive 5xx from the same endpoint.
 *
 * Usage:
 *   import { trackApiResponse } from './ErrorReporter';
 *   const res = await fetch('/api/emails');
 *   trackApiResponse('/api/emails', res.status);
 */
export function trackApiResponse(endpoint, status) {
  if (status >= 500) {
    const count = (apiFailures.get(endpoint) || 0) + 1;
    apiFailures.set(endpoint, count);
    if (count === 3) {
      sendBugReport({
        title: `API ${status} on ${endpoint} (3 consecutive failures)`,
        description: `Endpoint ${endpoint} returned ${status} three times in a row`,
        errorType: 'ApiFailure',
        severity: 'high',
        url: endpoint,
      });
      apiFailures.set(endpoint, 0); // reset after reporting
    }
  } else {
    apiFailures.delete(endpoint); // reset on success
  }
}

// React Error Boundary
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    sendBugReport({
      title: `React: ${error.name}: ${error.message}`.substring(0, 500),
      description: `Component stack: ${errorInfo.componentStack?.substring(0, 1000)}`,
      stackTrace: error.stack,
      errorType: error.name || 'ReactError',
      filePath: errorInfo.componentStack?.match(/at (\S+)/)?.[1],
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: '-apple-system, sans-serif' }}>
          <h2 style={{ color: '#1a1a1a', fontSize: 20, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#737373', fontSize: 14, marginBottom: 16 }}>
            This error has been automatically reported and our AI agent is working on a fix.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ padding: '8px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
          >
            Reload Page
          </button>
          {this.state.error && (
            <pre style={{ marginTop: 20, padding: 16, background: '#f5f5f5', borderRadius: 8, fontSize: 11, color: '#737373', textAlign: 'left', overflow: 'auto', maxHeight: 200 }}>
              {this.state.error.message}
              {'\n'}
              {this.state.error.stack?.split('\n').slice(0, 5).join('\n')}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
